// The DPDP notice, presented as its own thing, and the three acceptances beneath it.
//
// Wave C items C3.2 and C3.4. The notice used to be a hyperlink inside a checkbox label -
// "as described in the Terms & Conditions and Privacy Policy" - which is the arrangement
// Rule 3 rules out: the notice has to be presented independently of other information,
// not folded into a sentence about something else. So it gets its own bordered block,
// stating in plain words what is collected, why, and the one processing operation a user
// would most want to know about before signing up, with the full policy a click away.
//
// Shared by the signup form and the re-consent modal deliberately. They ask the same
// three questions and must ask them the same way; two copies of this wording would
// disagree the first time either was edited - which is how the Privacy Policy, the DPIA
// and the export ended up listing three different processor sets before B6.
import React from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { SIGNUP_ACKNOWLEDGEMENTS } from '../utils/signupAcknowledgements';

const AcknowledgementChecklist = ({ acknowledgements, onChange, disabled = false, idPrefix = 'ack' }) => {
  const toggle = (key, checked) => onChange({ ...acknowledgements, [key]: checked });

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-accent-main bg-background-light p-3">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-main" />
          <div className="text-xs text-primary-dark space-y-1">
            <p className="m-0 font-semibold">How Eternal Locker will use your data</p>
            <p className="m-0">
              Nybble Technologies stores your records and files encrypted, and uses your email
              address to sign you in and send account notices.
            </p>
            {/* The headline processing operation, said before signup rather than discovered
                after it. Finding F1 was that this feature appeared nowhere a user would look;
                putting it behind a link here would repeat that in miniature.
                SHORTENED FOR EL-29, and what survives the shortening is deliberate: the
                TRIGGER (a 20-day gap - the surprising part, and meaningless without its
                duration), the WARNING (reminders go out before anyone gets access, so this
                does not happen silently), the CONSEQUENCE (named third parties read the
                locker) and the CONTROL (it can be switched off). What went is the operating
                detail: which days the reminders go out, that answering one stops the clock,
                and the confirmation link they carry. All of it is in Privacy Policy s7,
                which this block links, and none of it is what makes this consent informed. */}
            <p className="m-0">
              <strong>If you stop logging in for 20 days</strong>, the nominees you name are given
              read access to your locker. We email you reminders before that happens, and you can
              switch this off at any time.
            </p>
            <p className="m-0">
              Withdraw consent, export your data or close your account from your profile.
            </p>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-accent-main hover:underline"
            >
              Read the full Privacy Notice <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      {SIGNUP_ACKNOWLEDGEMENTS.map(({ key, label, hint, href, hrefLabel }) => (
        <label
          key={key}
          htmlFor={`${idPrefix}-${key}`}
          className="flex items-start gap-2 text-xs text-text-muted cursor-pointer"
        >
          <input
            id={`${idPrefix}-${key}`}
            type="checkbox"
            checked={Boolean(acknowledgements?.[key])}
            onChange={(e) => toggle(key, e.target.checked)}
            disabled={disabled}
            className="mt-0.5 h-4 w-4 accent-accent-main flex-shrink-0"
          />
          <span>
            <span className="text-primary-dark">{label}</span>
            {hint && <span className="block text-text-muted">{hint}</span>}
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-accent-main hover:underline"
              >
                {hrefLabel}
              </a>
            )}
          </span>
        </label>
      ))}
    </div>
  );
};

export default AcknowledgementChecklist;
